import React, { useState } from 'react'
import styled from 'styled-components'
import { Column, Row } from 'components/common/layout'
import { DescText, H4Text, H5Text, SemiBoldText } from 'components/common/text'
import { StartupLogo } from 'components/startup/startup-logo'
import { OutlinedCard } from 'components/common/card'
import { StartupTableInfo } from 'components/startup/startup-table-info'
import { TextViewer } from 'components/common/text-editor'
import { TeamMember } from 'components/startup/team-member'
import { StartupImages } from 'components/startup/startup-images'
import { FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { theme } from 'infra/theme'
import { Tag, TagContainer } from 'components/common/tag'
import { getConditionSatisfactionPercentage } from 'utils/formatters'
import { getDueDateMessage } from 'utils/displays'
import { displayPositionName } from 'utils/displays'
import { getProposalMessage } from 'utils/displays'
import { ConditionSatisfaction } from 'components/scouts/chat/condition-satisfaction'
import {
  Condition,
  DashboardTempScoutProposal,
  Range,
  ScoutPosition,
  ScoutProposalDetail,
  StartupDetailForProposal,
} from 'types/common'
import { observer } from 'mobx-react'
import { MyLink } from 'components/common/link'
import { useRouter } from 'next/router'
import { useIsDesktop } from 'utils/hooks'
import { useStores } from 'stores/globals'
import { Notice } from 'components/scouts/request-form/notice'
import { BtnSize, BtnType, Button } from 'components/common/button'
import { LineLimitedText } from 'components/scouts/profile-detail/desc-more-view'
import { Card } from 'components/common/card'
import { JOB_HELPER_STARTUP_ID } from 'infra/constants'
import toast from 'react-hot-toast'

export const ConditionSection: React.FC<{
  proposal: DashboardTempScoutProposal | ScoutProposalDetail
  importantConditions: Condition[]
  normalConditions: Condition[]
}> = observer(({ proposal, importantConditions, normalConditions }) => {
  return (
    <Section>
      <Center>
        <Header>
          내 조건에{' '}
          <strong style={{ color: theme.color.primary }}>
            {getConditionSatisfactionPercentage(
              [...importantConditions, ...normalConditions],
              proposal.fulfillments,
            )}
            %
          </strong>{' '}
          만족해요
        </Header>
      </Center>
      <StartupInfoCard style={{ borderRadius: 8 }}>
        <ConditionSatisfaction
          normalConditions={normalConditions}
          importantConditions={importantConditions}
          fulfillments={proposal.fulfillments}
        />
      </StartupInfoCard>
    </Section>
  )
})

export interface ProposedPosition extends ScoutPosition {
  experienceRange?: Range | null
}

export const ProposalDetail: React.FC<{
  startup: StartupDetailForProposal
  username: string
  proposal: DashboardTempScoutProposal | ScoutProposalDetail
  proposedPositions: ProposedPosition[]
  importantConditions: Condition[]
  normalConditions: Condition[]
  isExample?: boolean // greeting 페이지에서 가짜 데이터로 예시 보여주는 상황인지
  startupStatus?: {
    isPublished: boolean // 기업 상세정보 공개 유무
    isInfoFillRequest: boolean // 기업 상세정보 대신 작성 요청 여부
  } // 기업 사이드에서만 받음
}> = observer(
  ({
    startup,
    username,
    proposal,
    proposedPositions,
    importantConditions,
    normalConditions,
    isExample,
    startupStatus,
  }) => {
    const [isDetail, setIsDetail] = useState(false)
    const router = useRouter()
    const isDesktop = useIsDesktop()
    const { positionModalStore } = useStores()
    const proposalMessage =
      'introPerson' in proposal
        ? getProposalMessage(proposal)
        : proposal.message
    const startupLogoThumbnail = startup.thumbnail || '/no-startup-logo.svg'
    return (
      <Container>
        <Section>
          <HeaderWrapper>
            <StartupLogo src={startupLogoThumbnail} size={56} />
            <Header>
              <strong>{startup.name}</strong>에서 <br />
              새로운 메시지가 도착했어요
            </Header>
            {!isExample && !startupStatus && (
              <Notice>
                {getDueDateMessage(
                  'dueDate' in proposal ? proposal.dueDate : undefined,
                )}
              </Notice>
            )}
          </HeaderWrapper>
          <OutlinedCard style={{ padding: 16, gap: 24 }}>
            {isExample ? (
              <LineLimitedText text={proposalMessage} lineClamp={6} />
            ) : (
              <MessageText>
                {proposalMessage.replaceAll('#{인재이름}', username)}
              </MessageText>
            )}
          </OutlinedCard>
          {startupStatus?.isPublished === false && (
            <PublishNoticeText>
              {`아직 기업 정보가 작성되지 않았어요.\n${
                startupStatus.isInfoFillRequest ? '그룹바이가 대신 ' : ''
              }기업 정보 작성 후, 인재에게 실제로 보내진 메시지에서는 아래 내용이 제대로 보여요.`}
            </PublishNoticeText>
          )}
          {JOB_HELPER_STARTUP_ID !== startup.id && (
            <StartupTableInfo
              data={{
                ...startup,
                funding:
                  startup.fundings && startup.fundings.length > 0
                    ? {
                        amount: startup.fundings[0].amount,
                        round: startup.fundings[0].round,
                        attractedAt: startup.fundings[0].attractedAt || '',
                      }
                    : undefined,
              }}
            />
          )}
        </Section>
        {proposedPositions.length > 0 && (
          <Section>
            <Center>
              <Header>{username}님에게 맞는 포지션</Header>
            </Center>
            <Grid>
              {proposedPositions.map((position) => (
                <PositionCard
                  key={position.id}
                  style={{ gap: 8, cursor: 'pointer' }}
                  onClick={() => {
                    isDesktop
                      ? positionModalStore.openModal(position.id)
                      : router.push(`/positions/${position.id}`)
                  }}
                >
                  <SemiBoldText style={{ fontSize: 16 }}>
                    {displayPositionName(
                      position.name,
                      position.careerType,
                      position.experienceRange || null,
                    )}
                  </SemiBoldText>
                  <TagContainer
                    style={{
                      flex: 1,
                      alignContent: 'flex-start',
                      maxHeight: 66,
                      overflow: 'hidden',
                    }}
                  >
                    {position.techStacks.map((tech, index) => (
                      <Tag key={index}>{tech}</Tag>
                    ))}
                  </TagContainer>
                </PositionCard>
              ))}
            </Grid>
          </Section>
        )}
        <ConditionSection
          proposal={proposal}
          normalConditions={normalConditions}
          importantConditions={importantConditions}
        />
        <Section>
          <Center>
            <Header>
              <strong>{startup.name}</strong>에 대해 알려드릴게요
            </Header>
          </Center>
          <StartupInfoCard>
            <StartupInfoTitle>어떤 회사인가요?</StartupInfoTitle>
            <TextViewer value={startup.whatKindCompany} />
          </StartupInfoCard>
          {isDetail ? (
            <>
              {startup.curOutcome && (
                <StartupInfoCard>
                  <StartupInfoTitle>현재까지 성과</StartupInfoTitle>
                  <TextViewer value={startup.curOutcome} />
                </StartupInfoCard>
              )}
              <StartupInfoCard>
                <StartupInfoTitle>팀</StartupInfoTitle>
                {startup.members.map((member) => (
                  <TeamMember
                    key={member.id}
                    data={member}
                    backgroundColor='#FAFAFA'
                  />
                ))}
              </StartupInfoCard>
              <StartupImages images={startup.images} width={718} />
              {startup.reasonForInvesting && (
                <StartupInfoCard>
                  <StartupInfoTitle>투자 받은 이유</StartupInfoTitle>
                  <TextViewer value={startup.reasonForInvesting} />
                </StartupInfoCard>
              )}
            </>
          ) : (
            <OutlinedCard
              style={{ padding: 8, cursor: 'pointer' }}
              onClick={() => setIsDetail(true)}
            >
              <Row style={{ gap: 4, justifyContent: 'center' }}>
                <H5Text style={{ color: theme.color.black4 }}>
                  상세정보 더보기
                </H5Text>
                <FiChevronDown size={24} color={theme.color.black4} />
              </Row>
            </OutlinedCard>
          )}
        </Section>
        {isDetail && !isExample && (
          <Center>
            <MyLink
              onClick={(e) => {
                if (startupStatus?.isPublished === false) {
                  e.preventDefault()
                  toast(
                    '인재가 실제로 받은 메시지에서는 기업 정보 페이지로 이동해요.',
                  )
                }
              }}
              href={`/startups/${startup.id}`}
              newTab
            >
              <Button
                variant={BtnType.PRIMARY2}
                size={isDesktop ? BtnSize.LARGE : BtnSize.MEDIUM}
                style={{ width: 'fit-content' }}
              >
                <Row style={{ gap: 10, justifyContent: 'center' }}>
                  <StartupLogo src={startupLogoThumbnail} size={32} />
                  <BtnText>{startup.name}에 대해 더 알아보기</BtnText>
                  <FiChevronRight />
                </Row>
              </Button>
            </MyLink>
          </Center>
        )}
      </Container>
    )
  },
)

const PublishNoticeText = styled(H5Text)`
  padding: 40px 0;
  text-align: center;
  color: ${(p) => p.theme.color.primary};
  font-weight: ${(p) => p.theme.fontWeight.medium};
  line-height: 1.4;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.default};
    padding: 24px 0;
  }
`

const Container = styled(Column)`
  gap: 64px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    gap: 48px;
  }
`

const BtnText = styled.span`
  font-size: ${(p) => p.theme.fontSize.desc};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.default};
  }
`

const PositionCard = styled(Card)`
  padding: 16px;
  border-radius: 8px;
  background-color: ${(p) => p.theme.color.background2};
  :hover {
    background: ${(p) =>
      `linear-gradient(${p.theme.color.white1}52, ${p.theme.color.white1}52), ${p.theme.color.background2}`};
  }
`

const Section = styled(Column)`
  gap: 16px;
`

const Center = styled(Section)`
  align-items: center;
`

const HeaderWrapper = styled(Center)`
  gap: 16px;
  padding-bottom: 32px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding-bottom: 8px;
  }
`

const Header = styled(H4Text)`
  text-align: center;
  line-height: 1.5;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.h5};
  }
  @media ${(p) => p.theme.deviceSize.desktop} {
    br {
      display: none;
    }
  }
`

const MessageText = styled(DescText)`
  font-weight: ${(p) => p.theme.fontWeight.medium};
  color: #555555;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.default};
    line-height: 25px;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    grid-template-columns: 1fr;
  }
`

const StartupInfoCard = styled(OutlinedCard)`
  gap: 16px;
  border-radius: 16px;
  padding: 32px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 16px;
  }
`

const StartupInfoTitle = styled(SemiBoldText)`
  font-size: 20px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) => p.theme.fontSize.h5};
  }
`
